import { useCallback, useMemo } from "react";
import { useAppDispatch } from "hooks/redux";
import {
  websiteBuilderBringToBackContainer,
  websiteBuilderBringToFrontContainer,
  websiteBuilderDeleteNode,
} from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { isWebsiteBuilderLayoutContainer, TWebsiteBuilderContainer } from "../../../nodes/container/websiteBuilderContainer";
import { cloneDeep } from "lodash";
import {
  TWebsiteBuilderSection,
  websiteBuilderGenerateSectionBounds,
} from "../../../nodes/section/websiteBuilderSection";
import { IWebsiteBuilderClipboard } from "../../clipboard/useWebsiteBuilderClipboard";
import { isWebsiteBuilderStretchElementData } from "../../../nodes/element/websiteBuilderElement";
import { TWebsiteBuilderDevice } from "../../../website/shared/device";
import { isWebsiteBuilderFlex } from "../../../nodes/container/websiteBuilderFlex";
import { isWebsiteBuilderGrid } from "../../../nodes/container/websiteBuilderGrid";
import { isWebsiteBuilderBox } from "../../../nodes/container/websiteBuilderBox";
import { IWebsiteBuilderFreeformParentContext, TWebsiteBuilderNudgeSectionEntityInFreeformDirection, websiteBuilderCanBringToBackInFreeformParent, websiteBuilderCanBringToFrontInFreeformParent, websiteBuilderDuplicateSectionEntityInFreeform, websiteBuilderInsertSectionEntityInFreeform, websiteBuilderNudgeSectionEntityInFreeform, websiteBuilderPasteSectionEntityInFreeform, websiteBuilderGetFreeformParentNextZIndex } from "../../../nodes/websiteBuilderFreeformParentNode";
import { TWebsiteBuilderActivePageDomNodeRefMap } from "../../activePageDomNodeRefMap/useActivePageDomNodeRefMapRegistration";
import { TWebsiteBuilderScrollContainerRef } from "../../../WebsiteBuilderEditor";
import { websiteBuilderInsertElementIntoFlexContainer, websiteBuilderInsertElementIntoGridContainer } from "../helpers/sectionEntityOperations";
import { websiteBuilderSanitize } from "../../../sanitization/websiteBuilderSanitize";
import { isWebsiteBuilderTabset } from "../../../nodes/container/websiteBuilderTabset";
import { websiteBuilderGenerateTabDimensions } from "../../../nodes/tab/websiteBuilderTab";
import { TWebsiteBuilderTabsetActiveTabMap } from "../../../websiteBuilderEditorContext";

type TWebsiteBuilderCopyContainer = (
  node: TWebsiteBuilderContainer
) => void;

type TWebsiteBuilderPasteAtContainer = (
  targetNode: TWebsiteBuilderContainer,
  targetParentNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
  tabsetActiveTabMap: TWebsiteBuilderTabsetActiveTabMap,
  activePageDomNodeRefMap: TWebsiteBuilderActivePageDomNodeRefMap,
  scrollContainerRef: TWebsiteBuilderScrollContainerRef,
) => void;

type TWebsiteBuilderDuplicateContainer = (
  node: TWebsiteBuilderContainer,
  parentNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderBringToFrontContainer = (
  node: TWebsiteBuilderContainer,
  parentNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderBringToBackContainer = (
  node: TWebsiteBuilderContainer,
  parentNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderDeleteContainer = (
  node: TWebsiteBuilderContainer,
  parentNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderCutContainer = (
  node: TWebsiteBuilderContainer,
  parentNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderNudgeContainer = (
  direction: TWebsiteBuilderNudgeSectionEntityInFreeformDirection,
  shift: boolean,
  node: TWebsiteBuilderContainer,
  parentNode: TWebsiteBuilderSection,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

export interface IWebsiteBuilderContainerOperations {
  copyContainer: TWebsiteBuilderCopyContainer;
  pasteAtContainer: TWebsiteBuilderPasteAtContainer;
  duplicateContainer: TWebsiteBuilderDuplicateContainer;
  bringToFrontContainer: TWebsiteBuilderBringToFrontContainer;
  bringToBackContainer: TWebsiteBuilderBringToBackContainer;
  deleteContainer: TWebsiteBuilderDeleteContainer;
  cutContainer: TWebsiteBuilderCutContainer;
  nudgeContainer: TWebsiteBuilderNudgeContainer;
}

export function useWebsiteBuilderContainerOperations(
  clipboard: IWebsiteBuilderClipboard,
): IWebsiteBuilderContainerOperations {
  const dispatch = useAppDispatch();

  const copyContainer: TWebsiteBuilderCopyContainer = useCallback(
    node => {
      clipboard.copy(node);
    },
    [clipboard],
  );

  const pasteAtContainer: TWebsiteBuilderPasteAtContainer = useCallback(
    (targetNode, targetParentNode, pageId, device, tabsetActiveTabMap, activePageDomNodeRefMap, scrollContainerRef) => {
      const copiedNode = clipboard.peek();

      if (
        copiedNode?.type === "element" && (
          !isWebsiteBuilderStretchElementData(copiedNode.element) ||
          !copiedNode.element.stretch.enabled
        ) &&
        isWebsiteBuilderLayoutContainer(targetNode)
      ) {
        if (isWebsiteBuilderBox(targetNode)) {
          const parentContext: IWebsiteBuilderFreeformParentContext = {
            node: targetNode,
            bounds: {
              desktop: targetNode.container.layout.desktop.dimensions,
              tablet: targetNode.container.layout.tablet.dimensions,
              mobile: targetNode.container.layout.mobile.dimensions,
            },
            ancestry: {
              sectionId: targetParentNode.id,
              containerId: targetNode.id,
              gridCellId: null,
              tabId: null,
            },
          };

          websiteBuilderPasteSectionEntityInFreeform(
            copiedNode,
            parentContext,
            pageId,
            device,
            activePageDomNodeRefMap,
            scrollContainerRef,
            dispatch,
          );
        } else if (isWebsiteBuilderFlex(targetNode)) {
          websiteBuilderInsertElementIntoFlexContainer(copiedNode, targetNode, targetParentNode.id, pageId, device, dispatch);
        } else if (isWebsiteBuilderGrid(targetNode)) {
          websiteBuilderInsertElementIntoGridContainer(copiedNode, targetNode, targetParentNode.id, pageId, device, dispatch);
        } else if (isWebsiteBuilderTabset(targetNode)) {
          const activeTabId = tabsetActiveTabMap.get(targetNode.id);
          const activeTab = targetNode.children.find(tab => tab.id === activeTabId);

          if (activeTab == null) {
            return;
          }

          const parentContext: IWebsiteBuilderFreeformParentContext = {
            node: activeTab,
            bounds: {
              desktop: websiteBuilderGenerateTabDimensions(targetNode, "desktop"),
              tablet: websiteBuilderGenerateTabDimensions(targetNode, "tablet"),
              mobile: websiteBuilderGenerateTabDimensions(targetNode, "mobile"),
            },
            ancestry: {
              sectionId: targetParentNode.id,
              containerId: targetNode.id,
              gridCellId: null,
              tabId: activeTab.id,
            },
          };

          websiteBuilderPasteSectionEntityInFreeform(
            copiedNode,
            parentContext,
            pageId,
            device,
            activePageDomNodeRefMap,
            scrollContainerRef,
            dispatch,
          );
        }
      } else if (copiedNode?.type === "container") {
        const parentContext: IWebsiteBuilderFreeformParentContext = {
          node: targetParentNode,
          bounds: {
            desktop: websiteBuilderGenerateSectionBounds(targetParentNode.section.layout, "desktop"),
            tablet: websiteBuilderGenerateSectionBounds(targetParentNode.section.layout, "tablet"),
            mobile: websiteBuilderGenerateSectionBounds(targetParentNode.section.layout, "mobile"),
          },
          ancestry: {
            sectionId: targetParentNode.id,
            containerId: null,
            gridCellId: null,
            tabId: null,
          },
        };

        websiteBuilderInsertSectionEntityInFreeform(
          copiedNode,
          targetNode,
          parentContext,
          pageId,
          device,
          dispatch,
        );
      }
    },
    [clipboard],
  );

  const duplicateContainer: TWebsiteBuilderDuplicateContainer = useCallback((node, parentNode, pageId, device) => {
    const nodeToDuplicate: TWebsiteBuilderContainer = websiteBuilderSanitize(node);

    const parentContext: IWebsiteBuilderFreeformParentContext = {
      node: parentNode,
      bounds: {
        desktop: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "desktop"),
        tablet: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "tablet"),
        mobile: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "mobile"),
      },
      ancestry: {
        sectionId: parentNode.id,
        containerId: null,
        gridCellId: null,
        tabId: null,
      },
    };

    websiteBuilderDuplicateSectionEntityInFreeform(
      nodeToDuplicate,
      parentContext,
      pageId,
      device,
      dispatch,
    );
  }, []);

  const bringToFrontContainer: TWebsiteBuilderBringToFrontContainer = useCallback((node, parentNode, pageId, device) => {
    if (websiteBuilderCanBringToFrontInFreeformParent(node, parentNode)) {
      dispatch(
        websiteBuilderBringToFrontContainer(pageId, device, node.id, {
          id: node.id,
          prevZIndex: node.container.zIndex,
          nextZIndex: websiteBuilderGetFreeformParentNextZIndex(parentNode),
        }),
      );
    }
  }, []);

  const bringToBackContainer: TWebsiteBuilderBringToBackContainer = useCallback((node, parentNode, pageId, device) => {
    if (websiteBuilderCanBringToBackInFreeformParent(node, parentNode)) {
      dispatch(
        websiteBuilderBringToBackContainer(pageId, device, node.id, {
          id: node.id,
          parentId: parentNode.id,
          originalZIndex: node.container.zIndex,
          inverse: false,
        }),
      );
    }
  }, []);

  const deleteContainer: TWebsiteBuilderDeleteContainer = useCallback((node, parentNode, pageId, device) => {
    const index = parentNode.children.findIndex(child => child.id === node.id);

    if (index === -1) {
      return;
    }

    dispatch(
      websiteBuilderDeleteNode(pageId, device, parentNode.id, {
        snapshot: {
          inverseScrollNodeId: node.id,
          parentId: parentNode.id,
          node: cloneDeep(node),
          index,
        },
      }),
    );
  }, []);

  const cutContainer: TWebsiteBuilderCutContainer = useCallback(
    (node, parentNode, pageId, device) => {
      copyContainer(node);
      deleteContainer(node, parentNode, pageId, device);
    },
    [copyContainer, deleteContainer],
  );

  const nudgeContainer: TWebsiteBuilderNudgeContainer = useCallback((direction, shift, node, parentNode, pageId, device) => {
    const parentContext: IWebsiteBuilderFreeformParentContext = {
      node: parentNode,
      bounds: {
        desktop: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "desktop"),
        tablet: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "tablet"),
        mobile: websiteBuilderGenerateSectionBounds(parentNode.section.layout, "mobile"),
      },
      ancestry: {
        sectionId: parentNode.id,
        containerId: null,
        gridCellId: null,
        tabId: null,
      },
    };

    websiteBuilderNudgeSectionEntityInFreeform(
      direction,
      shift,
      node,
      parentContext,
      pageId,
      device,
      dispatch,
    );
  }, []);

  const containerOperations: IWebsiteBuilderContainerOperations = useMemo(
    () => ({
      copyContainer,
      pasteAtContainer,
      duplicateContainer,
      bringToFrontContainer,
      bringToBackContainer,
      deleteContainer,
      cutContainer,
      nudgeContainer,
    }),
    [copyContainer, pasteAtContainer, duplicateContainer, bringToFrontContainer, bringToBackContainer, deleteContainer, cutContainer, nudgeContainer],
  );

  return containerOperations;
}
